import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";
import { useDjContext } from "../../context/DjContext";
import { formatDate } from "../../utils/formatDate";
import { STATUS } from "../constants/statuses";
import FilterComponent from "../../components/common/FilterComponent";
import ModalResponseComponent from "../../components/ui/ModalResponseComponent";
import { sortRequests } from "../../utils/sortRequests";
import { updateRequestStatus as createUpdateRequestStatus } from "../../handlers/requestHandler";
import { handlePlayed as createHandlePlayed } from "../../handlers/requestHandler";
import { checkAndAddStatusNotifications } from "notifications/notificationChecker";
import {
  CheckCircleIcon,
  Clock4,
  ClockIcon,
  PlayIcon,
  XCircleIcon,
} from "lucide-react";

function DJPanel({ refreshTrigger, notifications }) {
  const [requests, setRequests] = useState([]);
  const { djId, token, isOnline } = useDjContext();
  const [statusFilter, setStatusFilter] = useState("");
  const prevNotificationsRef = useRef([]);

  const fetchRequests = async () => {
    try {
      const response = await axiosInstance.get(`/dj/${djId}/requests`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          status: statusFilter || undefined,
        },
      });

      checkAndAddStatusNotifications(response.data, djId, true);
      const sortedData = sortRequests(response.data);
      setRequests(sortedData);
    } catch (error) {
      console.error("Failed to fetch requests:", error);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [djId, token, statusFilter, refreshTrigger]);

  const updateRequestStatus = createUpdateRequestStatus({ setRequests });

  useEffect(() => {
    if (!notifications.length) return;

    const newNotifications = notifications.filter(
      (n) => !prevNotificationsRef.current.some((prev) => prev.id === n.id),
    );

    if (
      newNotifications.some((n) =>
        [STATUS.PENDING, STATUS.PAID].some((status) =>
          n.message.includes(status),
        ),
      )
    ) {
      fetchRequests();
    }

    prevNotificationsRef.current = [...notifications];
  }, [notifications]);

  const [comment, setComment] = useState("");
  const [currentStatus, setCurrentStatus] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [artist, setArtist] = useState("");
  const [trackName, setTrackName] = useState("");
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(null);

  const handleApproveReject = (id, userId, status, artist, trackName) => {
    setCurrentRequestId(id);
    setCurrentStatus(status);
    setCurrentUserId(userId);
    setIsCommentModalOpen(true);
    setArtist(artist);
    setTrackName(trackName);
  };

  const handlePlayed = createHandlePlayed({
    isOnline,
    token,
    updateRequestStatus,
  });

  const handleSendComment = async () => {
    if (!isOnline) {
      toast.error("You are offline. Go online to make this action");
      return;
    }

    try {
      const messageData = {
        djMessage: comment.trim(),
        status: currentStatus,
      };

      await axiosInstance.patch(
        `/request/${currentRequestId}/update`,
        messageData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setRequests((prevRequests) => {
        if (currentStatus === STATUS.REJECTED) {
          return prevRequests.filter(
            (req) => String(req.id) !== String(currentRequestId),
          );
        }

        const updatedRequests = prevRequests.map((req) =>
          String(req.id) === String(currentRequestId)
            ? { ...req, status: currentStatus }
            : req,
        );

        return sortRequests(updatedRequests);
      });

      toast.success("Status updated successfully");
      setComment("");
      setIsCommentModalOpen(false);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(
          "Cannot approve the request. The time limit has passed (5 hours).",
        );
        return;
      } else {
        toast.error("Failed to update status");
      }
    }
  };

  return (
    <div className="min-h-screen">
      <div className="-px-8">
        <FilterComponent
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />
      </div>

      {requests.length === 0 ? (
        <p className="text-gray-500 text-center text-sm sm:text-base">
          No requests yet.
        </p>
      ) : (
        <div className="space-y-4">
          {requests.map((request, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-cardBg to-secondary text-white rounded-2xl shadow-lg py-4 px-2 flex flex-col space-y-3 relative"
            >
              {/* Date top-right */}
              <div className="absolute top-2 right-2 text-sm text-gray-500 flex items-center gap-1">
                {formatDate(request.createdAt)}
                <span className="text-accent text-lg ml-0.5">
                  <Clock4 className="size-[14px]" />
                </span>
              </div>

              {/* Top section: album art + main info */}
              <div className="flex gap-3 items-center pt-2">
                <img
                  src={request.albumArt}
                  alt={request.trackName}
                  className="w-24 h-24 object-cover rounded-md"
                />
                <div className="flex-1">
                  <h2 className="font-semibold text-base sm:text-lg">
                    {request.trackName} - {request.artist}
                  </h2>
                  <p className="text-textMuted text-sm pt-2">
                    From: {request.username}
                  </p>
                  <p className="text-textMuted text-sm">
                    Place: {request.place.name}
                  </p>
                  <p className="text-textMuted text-sm">
                    Price: {request.price} PLN
                  </p>
                </div>
              </div>

              {/* Message */}
              {request.message && (
                <div className="relative bg-black/50 p-3 rounded-lg text-sm text-gray-500 top-1">
                  <span className="absolute -top-2 left-4 text-textMuted text-xs px-2">
                    Message
                  </span>
                  <p className="text-sm text-gray-500">{request.message}</p>
                </div>
              )}

              {/* Buttons control */}
              <div className="flex flex-wrap gap-4 justify-center sm:justify-end mt-4">
                {request.status === STATUS.PENDING && (
                  <>
                    <button
                      onClick={() =>
                        handleApproveReject(
                          request.id,
                          request.userId,
                          STATUS.REJECTED,
                          request.artist,
                          request.trackName,
                        )
                      }
                      className="bg-red-500 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 mt-2"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() =>
                        handleApproveReject(
                          request.id,
                          request.userId,
                          STATUS.APPROVED,
                          request.artist,
                          request.trackName,
                        )
                      }
                      className="bg-green-500 text-black px-4 py-2 rounded-md text-sm hover:bg-green-700 mt-2"
                    >
                      Approve
                    </button>
                  </>
                )}

                {request.status === STATUS.APPROVED && (
                  <div className="flex items-center gap-1 text-yellow-400 text-sm mt-3">
                    <ClockIcon className="w-4 h-4" />
                    Waiting for payment
                  </div>
                )}

                {request.status === STATUS.PAID && (
                  <button
                    onClick={() =>
                      handlePlayed(
                        request.id,
                        request.userId,
                        request.artist,
                        request.trackName,
                      )
                    }
                    className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm mt-2"
                  >
                    <PlayIcon className="w-4 h-4" />
                    Play
                  </button>
                )}

                {request.status === STATUS.PLAYED && (
                  <div className="flex items-center gap-1 text-green-500 text-sm mt-3">
                    <CheckCircleIcon className="w-4 h-4" />
                    Played
                  </div>
                )}

                {request.status === STATUS.REJECTED && (
                  <div className="flex items-center gap-1 text-red-500 text-sm mt-3">
                    <XCircleIcon className="w-4 h-4" />
                    Rejected
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isCommentModalOpen && (
        <ModalResponseComponent
          comment={comment}
          handleSendComment={handleSendComment}
          setIsCommentModalOpen={setIsCommentModalOpen}
          setComment={setComment}
        />
      )}
    </div>
  );
}

export default DJPanel;
